<template>
  <create-account-layout
    :current-step="3"
    title="Request Access"
  >
    <p class="mb-7 text-left text-dark-gray">
      For you to be able to join {{ carrierName }}, we need an authorized
      representative of the carrier to grant you access. You will be able to
      continue when this request is approved by the carrier.
    </p>
    <section
      v-if="!isAliasRoute"
      class="mb-7"
    >
      <form-field
        label="Full Name"
        :value="full_name"
        placeholder="Enter First and Last Name"
        :on-input="handleFieldUpdate"
        :error="full_name_error"
        :alert-message="full_name_alert"
        name="full_name"
      />
      <form-field
        label="Company Email"
        :value="email_address"
        placeholder="Enter Company Email Address"
        :on-input="handleFieldUpdate"
        :on-blur="checkEmailAddress"
        :error="email_address_error"
        name="email_address"
      />
      <form-field
        label="Dispatch Service Company name"
        :value="form.company_name.val"
        placeholder="Enter your Dispatch Company Name"
        :on-input="handleUpdate"
        name="company_name"
      />
    </section>
    <NotFMSCABox v-if="fmscaIsEmpty">
      No unique FMCSA contact methods found. Please ask an authorized
      representative from the carrier to call us at 906-HIGHWAY or email us at
      service@highway.com for further assistance.
    </NotFMSCABox>
    <section v-else-if="!maskedContacts.length">
      <contact-recipients
        v-if="contactRecipients.length"
        title="Recipients of request"
        :items-to-show="contactRecipients.length"
        :show-see-more="false"
        :contacts="contactRecipients"
      />
    </section>
    <section v-else>
      <request-recipients
        title="Recipients of request"
        :items="maskedContacts"
      />
    </section>
    <button-main
      :disabled="!allFormFieldsFilled || formHasErrors || fmscaIsEmpty"
      class="mt-3"
      :is-loading="loading"
      @click="handleSubmit"
    >
      Send Request
    </button-main>
  </create-account-layout>
</template>

<script>
// Components
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';
import NotFMSCABox from '@/components/NotFMSCABox.vue';
import CreateAccountLayout from '@/components/layouts/CreateAccountLayout.vue';
import RequestRecipients from './RequestRecipients.vue';
import ContactRecipients from './ContactRecipients.vue';

// Common
import { formatFirstLastName } from '@/common/validators';
import { toastResponse } from '@/common/handleServerResponse';

//Mixin
import fullnameAndEmailField from '@/mixins/fullnameAndEmailField';

export default {
  name: 'DispatcherGeneralInformation',
  components: {
    ButtonMain,
    FormField,
    ContactRecipients,
    RequestRecipients,
    CreateAccountLayout,
    NotFMSCABox,
  },
  mixins: [fullnameAndEmailField],
  data() {
    return {
      form: {
        company_name: {
          val: '',
          error: false,
        },
      },
      contact_recipients: [],
      loading: false,
    };
  },
  computed: {
    storedDispatchServiceCompanyName() {
      return (
        this.$store.state.dispatchers.dispatchServiceCompanyName ||
        localStorage.getItem('dispatchServiceCompanyName')
      );
    },
    fullName() {
      return `${this.$store.state.users.currentUser?.first_name} ${this.$store.state.users.currentUser?.last_name}`;
    },
    emailAddress() {
      return this.$store.state.users.currentUser?.email;
    },
    isAliasRoute() {
      return (
        this.$route.fullPath ===
        '/sign-up/dispatcher/request-access-general-information'
      );
    },
    clientId() {
      return this.$route.query.client_id;
    },
    carrierName() {
      return this.$store.state.carriers?.carrierDetails?.legal_name || '';
    },
    carrierId() {
      return this.$store.state.carriers?.carrierDetails?.id || '';
    },
    maskedContacts() {
      return this.$store.state.carriers?.maskedContacts || [];
    },
    contactRecipients() {
      const carrier = this.$store.state.carriers?.carrierDetails;
      return carrier?.email_addresses || [];
    },
    fmscaIsEmpty() {
      return !this.maskedContacts.length && !this.contactRecipients.length;
    },
    allFormFieldsFilled() {
      const { company_name } = this.form;
      return !!(this.full_name && company_name.val && this.email_address);
    },
    formHasErrors() {
      const { company_name } = this.form;
      return !!(
        this.full_name_error_sw ||
        company_name.error ||
        this.email_address_error
      );
    },
    userRequestedCarrierId() {
      return this.$store.state.users?.currentUser?.requested_carrier_id || null;
    },
    isDispatcherChallengeRequired() {
      return (
        this.$store.state.verifications
          ?.dispatcher_challenge_question_required || false
      );
    },
  },
  watch: {
    userRequestedCarrierId: {
      handler: function (val) {
        let self = this;
        console.log('userRequestedCarrierId running: id == ' + val);
        if (val == '393180' && process.env.NODE_ENV == 'staging') {
          self.$store.commit(
            'verifications/setRequireChallengeDispatcher',
            true
          );
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const findInput = document.querySelector('#full_name');
    findInput?.focus();

    if (this.storedDispatchServiceCompanyName && this.isAliasRoute) {
      console.log('Setting dispatch service company name using alias.');
      this.form.company_name.val = this.storedDispatchServiceCompanyName;

      // This is being done to bypass the function we have in the mixin and automatically load all the information.
      this.handleFieldUpdate({
        target: {
          value: this.fullName,
          name: 'full_name',
        },
      });

      this.handleFieldUpdate({
        target: {
          value: this.emailAddress,
          name: 'email_address',
        },
      });

      console.log(
        'Values',
        this.form.company_name,
        this.fullName,
        this.emailAddress
      );
    }
  },
  async created() {
    await this.$store.dispatch('users/serverRefreshCurrentUser');
    let currentUser = this.$store.state.users.currentUser;
    // Redirect if the user is already associated with a company
    if (currentUser?.company?.id) {
      this.$router.push({ name: 'Home' });
    }
  },
  methods: {
    getMessageId(message) {
      switch (message) {
        case 'must be present':
          return 1;
        case 'must be dispatcher to create packet':
          return 1;
        case 'no dispatcher connection present':
          return 1;
        case 'dispatcher connection awaiting approval':
          return 2;
        case 'dispatcher connection was not approved by the carrier':
          return 3;
        default:
          break;
      }
    },
    handleUpdate(e) {
      this.form[e.target.name].val = e.target.value;
    },
    async createDispatcherConnection() {
      console.log('creating dispatcher connection');
      const res = await this.$store.dispatch(
        'dispatchers/createDispatcherConnection',
        {
          carrier_id: this.carrierId,
        }
      );
      if ([200, 201].includes(res.status)) {
        await this.redirectToCreateDispatcherOnboardingPacket();
      } else {
        // handle error for updating user details
        toastResponse(this, res, null);
      }
    },
    // Redirect to OAuth 2 screen if able to
    // create the onboarding packet
    async redirectToCreateDispatcherOnboardingPacket() {
      await this.$store.dispatch('users/serverRefreshCurrentUser');

      let body = {
        carrier_id: this.carrierId,
        client_id: this.$store.state.users.currentUser.id,
      };
      let res = await this.$store.dispatch(
        'onboardingPacket/createDispatcherOnBoardingPacket',
        body
      );

      //If the OnboardingPacket create endpoint return a 422 we need to redirect the user to the
      // verify screen.
      if (res.status === 422) {
        localStorage.setItem(
          'carrier_detail_id',
          this.$store.state.carriers?.carrierDetails?.id
        );
        this.$router.replace({
          path: `/dispatcher/awaiting-approval/${this.getMessageId(res.data.message[0])}`,
        });
      } else {
        console.log('creating onboarding packet');
        const clientId = localStorage.getItem('client_id');
        if (clientId == null) {
          // if there is not client_id in local storage, then
          // send the user to the dashboard
          return this.$router.push({ name: 'Dispatcher Dashboard' });
        }
        let redirectAfterSignIn = JSON.parse(
          localStorage.getItem('redirect_url_after_sigin')
        );
        // If they have a redirect_url_after_signin, take them
        // there else send them to the dashboard
        if (redirectAfterSignIn) {
          // Set the state.dispatchers.selectedCarrierId before
          // going back to OAuth screen in this special use case
          // where they chose the carrier when they signed up
          await this.$store.commit(
            'dispatchers/setSelectedCarrierId',
            this.carrierId
          );
          localStorage.removeItem('redirect_url_after_sigin');
          this.$router.push(redirectAfterSignIn);
        } else {
          return this.$router.push({ name: 'Dispatcher Dashboard' });
        }
      }
    },
    async createDispatchServiceCompany() {
      const res = await this.$store.dispatch('users/postCreateCompany', {
        legal_name: this.form.company_name.val,
        company_type_name: 'dispatcher',
      });
      if ([200, 201].includes(res.status)) {
        // send user to account success screen
        if (this.isDispatcherChallengeRequired) {
          this.$router.push({ name: 'DispatcherVerificationChallenge' });
        } else {
          await this.createDispatcherConnection();
        }
      } else {
        // handle error for updating user details
        toastResponse(this, res, null);
      }
    },
    async updateUserInfo() {
      const { first_name, last_name } = formatFirstLastName(this.full_name);
      const payload = {
        first_name,
        last_name,
        email: this.email_address,
      };
      const res = await this.$store.dispatch(
        'users/patchSignUpDetails',
        payload
      );
      if ([200, 201].includes(res.status)) {
        // send user to account success screen
        await this.createDispatchServiceCompany();
      } else {
        // handle error for updating user details
        toastResponse(this, res, null);
      }
    },
    async handleSubmit() {
      this.loading = true;
      try {
        await this.updateUserInfo();
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
